import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalContent,
  ModalOverlay,
  Text,
  VStack,
} from "@chakra-ui/react";
import { CloseIcon } from "@chakra-ui/icons";
import { FaPlus, FaSearch } from "react-icons/fa";
import trashIconSrc from "../../assets/trash-icon.svg";
import type { InterestsAudienceData } from "@common/interfaces/KYDMarketing";
import { formatInterestsAudienceName } from "@common/utils/interestAudiences";
import {
  createDialogContext,
  DialogProvider,
  useDialogWithOptions,
} from "src/Common/Dialog";
import { SELECTED_AUDIENCES_STYLES } from "../selectedAudiencesPanelStyles";

export const CreateMetaInterestsAudienceDialogContext = createDialogContext();

export type InterestSearchResult = { id: string; name: string };

export type CreateMetaInterestsAudienceDialogProps = {
  isOpen: boolean;
  close: () => void;
  searchInterests: (query: string) => Promise<{ data: InterestSearchResult[] }>;
  onSubmit: (audience: InterestsAudienceData) => void;
  /** When set, dialog edits an existing bundle instead of creating a new one. */
  editAudienceId?: string | null;
  initialInterests?: { key: string; value: string }[];
};

const styles = SELECTED_AUDIENCES_STYLES;

/** Stable default — inline `= []` in props creates a new array every render and retriggers effects. */
export const EMPTY_INITIAL_INTERESTS: { key: string; value: string }[] = [];

const interestRowTextSx = {
  fontFamily: styles.fontFamily,
  fontSize: styles.interestSearchResultTextSize,
  fontWeight: styles.interestSearchResultTextWeight,
  color: styles.titleColor,
  lineHeight: styles.interestSearchResultTextLineHeight,
} as const;

const iconActionSx = {
  borderColor: "#e2e2e2",
  bg: styles.actionBg,
  borderRadius: styles.actionRadius,
  px: styles.actionPx,
  py: styles.actionPy,
  minW: "auto",
  h: "auto",
  fontSize: 0,
} as const;

const INTEREST_ADD_BUTTON_SIZE = "38px";

function InterestSearchResultRow({
  name,
  isDisabled,
  onAdd,
}: {
  name: string;
  isDisabled: boolean;
  onAdd: () => void;
}) {
  return (
    <Flex
      align="center"
      justify="space-between"
      w="full"
      minH={INTEREST_ADD_BUTTON_SIZE}
      flexShrink={0}
      px={styles.interestSearchResultRowPx}
      borderRadius={styles.interestSearchResultRowRadius}
    >
      <Text {...interestRowTextSx} noOfLines={1} flex={1} minW={0} pr={2}>
        {name}
      </Text>
      <Button
        aria-label={isDisabled ? "Already added" : `Add ${name}`}
        variant="outline"
        borderWidth="1px"
        borderColor="#e2e2e2"
        bg={styles.actionBg}
        borderRadius={styles.actionRadius}
        flexShrink={0}
        minW={INTEREST_ADD_BUTTON_SIZE}
        w={INTEREST_ADD_BUTTON_SIZE}
        h={INTEREST_ADD_BUTTON_SIZE}
        minH={INTEREST_ADD_BUTTON_SIZE}
        maxH={INTEREST_ADD_BUTTON_SIZE}
        p={0}
        lineHeight={1}
        isDisabled={isDisabled}
        onClick={onAdd}
        _focusVisible={{
          boxShadow: "0 0 0 2px #1d74ff",
        }}
      >
        <FaPlus size={14} color="#1a202c" />
      </Button>
    </Flex>
  );
}

function InterestSelectedChip({
  label,
  onRemove,
}: {
  label: string;
  onRemove: () => void;
}) {
  return (
    <Flex
      align="center"
      gap={`${styles.interestSelectedChipGapPx}px`}
      bg={styles.interestSelectedChipBg}
      border={styles.interestSelectedChipBorder}
      borderRadius={styles.interestSelectedChipRadius}
      px={styles.interestSelectedChipPx}
      py={styles.interestSelectedChipPy}
      flexShrink={0}
      maxW="full"
    >
      <Text {...interestRowTextSx} noOfLines={1}>
        {label}
      </Text>
      <IconButton
        aria-label={`Remove ${label}`}
        icon={
          <Image
            src={trashIconSrc}
            alt=""
            w={styles.trashIconW}
            h={styles.trashIconH}
            objectFit="contain"
          />
        }
        variant="ghost"
        minW="auto"
        h="auto"
        px={styles.actionPx}
        py={styles.actionPy}
        borderRadius={styles.actionRadius}
        onClick={onRemove}
      />
    </Flex>
  );
}

const CreateMetaInterestsAudienceDialog = ({
  isOpen,
  close,
  searchInterests,
  onSubmit,
  editAudienceId = null,
  initialInterests = EMPTY_INITIAL_INTERESTS,
}: CreateMetaInterestsAudienceDialogProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<InterestSearchResult[]>(
    []
  );
  const [selected, setSelected] = useState<{ key: string; value: string }[]>(
    []
  );
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  const resetState = useCallback(() => {
    setSearchQuery("");
    setSearchResults([]);
    setSelected([]);
    setSearchError(null);
    setHasSearched(false);
  }, []);

  const wasOpenRef = useRef(false);
  const initialInterestsOnOpenRef = useRef(initialInterests);

  useEffect(() => {
    if (!isOpen) {
      if (wasOpenRef.current) {
        resetState();
      }
      wasOpenRef.current = false;
      return;
    }

    if (!wasOpenRef.current) {
      initialInterestsOnOpenRef.current = initialInterests;
      setSelected(
        initialInterestsOnOpenRef.current.map((i) => ({
          key: i.key,
          value: i.value || i.key,
        }))
      );
      setSearchResults([]);
      setSearchError(null);
      setHasSearched(false);
    }
    wasOpenRef.current = true;
  }, [isOpen, resetState]);

  const runSearch = useCallback(
    async (query: string) => {
      const q = query.trim();
      if (!q) {
        setSearchResults([]);
        return;
      }
      setIsSearching(true);
      setSearchError(null);
      setHasSearched(true);
      try {
        const res = await searchInterests(q);
        setSearchResults(res.data ?? []);
      } catch (err) {
        setSearchResults([]);
        setSearchError(
          err instanceof Error ? err.message : "Could not search interests"
        );
      } finally {
        setIsSearching(false);
      }
    },
    [searchInterests]
  );

  const isSelected = useCallback(
    (id: string) => selected.some((s) => s.key === id),
    [selected]
  );

  const addInterest = useCallback((item: InterestSearchResult) => {
    setSelected((prev) => {
      if (prev.some((s) => s.key === item.id)) return prev;
      return [...prev, { key: item.id, value: item.name }];
    });
  }, []);

  const removeInterest = useCallback((key: string) => {
    setSelected((prev) => prev.filter((s) => s.key !== key));
  }, []);

  const handleClose = () => {
    resetState();
    close();
  };

  const handleSubmit = () => {
    if (selected.length === 0) return;
    onSubmit({
      ...(editAudienceId ? { id: editAudienceId } : {}),
      audience_type: "interests",
      name: formatInterestsAudienceName(selected),
      interests: selected,
      created_at: new Date().toISOString(),
    });
    handleClose();
  };

  const isEdit = Boolean(editAudienceId);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="2xl"
      isCentered
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent
        bg="white"
        borderRadius="lg"
        maxW="816px"
        p={0}
        fontFamily={styles.fontFamily}
        data-testid="create-meta-interests-audience-dialog"
      >
        <Box px={6} pt={6} pb={2}>
          <Flex justify="space-between" align="flex-start">
            <Box flex={1} pr={4}>
              <Text
                fontSize="20px"
                fontWeight={600}
                color={styles.titleColor}
                lineHeight={1.3}
              >
                Create audience from Meta Interests
              </Text>
              <Text
                fontSize="sm"
                fontWeight={500}
                color={styles.subtitleColor}
                mt={1}
              >
                Add all interests you want to target an audience from
              </Text>
            </Box>
            <IconButton
              aria-label="Close"
              icon={<CloseIcon color="gray.500" fontSize={12} />}
              onClick={handleClose}
              variant="ghost"
              size="sm"
            />
          </Flex>
        </Box>

        <Box
          mx={6}
          mb={4}
          p={6}
          border="1px solid"
          borderColor="gray.200"
          borderRadius="lg"
          bg="white"
        >
          <Flex justify="space-between" align="center" mb={4}>
            <Text fontWeight={600} fontSize="md" color={styles.titleColor}>
              Search and add interests
            </Text>
          </Flex>

          {selected.length > 0 ? (
            <Flex
              gap={`${styles.interestSelectedChipsWrapGapPx}px`}
              flexWrap="wrap"
              mb={4}
              data-testid="interests-selected-chips"
            >
              {selected.map((item) => (
                <InterestSelectedChip
                  key={item.key}
                  label={item.value}
                  onRemove={() => removeInterest(item.key)}
                />
              ))}
            </Flex>
          ) : null}

          <HStack spacing={2} mb={3} align="stretch">
            <InputGroup size="md" flex={1}>
              <InputLeftElement pointerEvents="none">
                <FaSearch color="gray.400" size={12} />
              </InputLeftElement>
              <Input
                placeholder={
                  selected.length > 0
                    ? "Search for interests"
                    : "Search or select multiple interests"
                }
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    void runSearch(searchQuery);
                  }
                }}
                bg="white"
                borderColor="gray.200"
                data-testid="interests-search-input"
              />
            </InputGroup>
            <Button
              flexShrink={0}
              variant="outline"
              isLoading={isSearching}
              onClick={() => void runSearch(searchQuery)}
              data-testid="interests-search-submit"
            >
              Search
            </Button>
          </HStack>

          {searchError ? (
            <Text fontSize="sm" color="red.500" mb={3} data-testid="interests-search-error">
              {searchError}
            </Text>
          ) : null}

          {hasSearched &&
          !isSearching &&
          !searchError &&
          searchResults.length === 0 ? (
            <Text fontSize="sm" color="gray.500" mb={3} data-testid="interests-search-empty">
              No interests found. Try another search term.
            </Text>
          ) : null}

          {searchResults.length > 0 ? (
            <Box
              maxH="240px"
              overflowY="auto"
              pr={2}
              sx={{ scrollbarGutter: "stable" }}
              data-testid="interests-search-results"
            >
              <Flex
                direction="column"
                gap={`${styles.interestListGapPx}px`}
                py={3}
                sx={{
                  // Keep full row height inside the scrollport (Chakra stacks can clip tall children).
                  "& > *": { flexShrink: 0 },
                }}
              >
                {searchResults.map((item) => (
                  <InterestSearchResultRow
                    key={item.id}
                    name={item.name}
                    isDisabled={isSelected(item.id)}
                    onAdd={() => addInterest(item)}
                  />
                ))}
              </Flex>
            </Box>
          ) : null}
        </Box>

        <Flex
          px={6}
          pb={6}
          justify="space-between"
          align="center"
          data-testid="interests-dialog-footer"
        >
          <Button variant="ghost" onClick={handleClose} fontWeight={600}>
            ← Back
          </Button>
          <Button
            bg="gray.900"
            color="white"
            _hover={{ bg: "black" }}
            isDisabled={selected.length === 0}
            onClick={handleSubmit}
            data-testid="add-interests-audience-submit"
          >
            {isEdit ? "Save Audience" : "Add Audience"}
          </Button>
        </Flex>
      </ModalContent>
    </Modal>
  );
};

export const CreateMetaInterestsAudienceDialogWrapper = ({
  searchInterests,
  onSubmit,
  editAudienceId,
  initialInterests,
}: {
  searchInterests: (query: string) => Promise<{ data: InterestSearchResult[] }>;
  onSubmit: (audience: InterestsAudienceData) => void;
  editAudienceId?: string | null;
  initialInterests?: { key: string; value: string }[];
}) => {
  const { isOpen, close } = useDialogWithOptions(
    CreateMetaInterestsAudienceDialogContext
  );

  return (
    <CreateMetaInterestsAudienceDialog
      isOpen={isOpen}
      close={close}
      searchInterests={searchInterests}
      onSubmit={onSubmit}
      editAudienceId={editAudienceId}
      initialInterests={initialInterests}
    />
  );
};

export const withCreateMetaInterestsAudienceDialog =
  <P extends object>(Component: React.ComponentType<P>) =>
  (
    props: P & {
      searchInterests?: (
        query: string
      ) => Promise<{ data: InterestSearchResult[] }>;
      onInterestsAudienceSubmit?: (audience: InterestsAudienceData) => void;
    }
  ) => {
    return (
      <DialogProvider context={CreateMetaInterestsAudienceDialogContext}>
        <Component {...props} />
        {props.searchInterests && props.onInterestsAudienceSubmit ? (
          <CreateMetaInterestsAudienceDialogWrapper
            searchInterests={props.searchInterests}
            onSubmit={props.onInterestsAudienceSubmit}
          />
        ) : null}
      </DialogProvider>
    );
  };

export default CreateMetaInterestsAudienceDialog;
